import { useCallback, useEffect, useMemo, useRef } from "react";
import { Field, FieldLabel } from "@/components/ui/field";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PagedCombobox } from "@/components/ui/pagedcombobox";

export type SeedlingCategory = "vegetable" | "agroforestry";

export interface CompanySeedlingCropTypeOption {
  value: string;
  label: string;
  isAgroforestry: boolean;
}

interface CompanySeedlingCategorySelectorProps {
  category: SeedlingCategory;
  onCategoryChange: (category: SeedlingCategory) => void;
  selectedValue: string;
  onSelectedValueChange: (value: string) => void;
  selectedLabel?: string;
  fetchOptions: (args: {
    search: string;
    pageNumber: number;
    pageSize: number;
    isAgroforestry: boolean;
  }) => Promise<{ items: CompanySeedlingCropTypeOption[] }>;
  disabled?: boolean;
  allowAllOption?: boolean;
  allOptionLabel?: string;
  cropTypeLabel?: string;
  required?: boolean;
  pageSize?: number;
  emphasizeTabs?: boolean;
}

export const CompanySeedlingCategorySelector = ({
  category,
  onCategoryChange,
  selectedValue,
  onSelectedValueChange,
  selectedLabel,
  fetchOptions,
  disabled,
  allowAllOption = false,
  allOptionLabel = "All Crop Types",
  cropTypeLabel = "Crop Type",
  required = true,
  pageSize = 25,
  emphasizeTabs = false,
}: CompanySeedlingCategorySelectorProps) => {
  const previousCategoryRef = useRef<SeedlingCategory>(category);

  // Clear stale crop type whenever the tab changes.
  useEffect(() => {
    if (previousCategoryRef.current !== category && selectedValue) {
      onSelectedValueChange("");
    }
    previousCategoryRef.current = category;
  }, [category, selectedValue, onSelectedValueChange]);

  const selectedItem = useMemo(
    () =>
      selectedValue
        ? {
            id: selectedValue,
            name: selectedLabel ?? selectedValue,
          }
        : null,
    [selectedValue, selectedLabel]
  );

  const fetchPage = useCallback(
    async ({ search, pageNumber, pageSize: fetchSize }: { search: string; pageNumber: number; pageSize: number }) => {
      const result = await fetchOptions({
        search,
        pageNumber,
        pageSize: fetchSize,
        isAgroforestry: category === "agroforestry",
      });

      return {
        items: result.items.map((item) => ({ id: item.value, name: item.label })),
      };
    },
    [fetchOptions, category]
  );

  return (
    <div className="space-y-3">
      <Tabs value={category} onValueChange={(v) => onCategoryChange(v as SeedlingCategory)}>
        <TabsList>
          <TabsTrigger
            value="vegetable"
            className={emphasizeTabs ? "font-semibold data-[state=active]:font-bold" : undefined}
          >
            Vegetables
          </TabsTrigger>
          <TabsTrigger
            value="agroforestry"
            className={emphasizeTabs ? "font-semibold data-[state=active]:font-bold" : undefined}
          >
            Agroforestry
          </TabsTrigger>
        </TabsList>
      </Tabs>

      <Field>
        <FieldLabel>
          {cropTypeLabel} {required && <span className="text-red-500">*</span>}
        </FieldLabel>
        <div className="space-y-2">
          {allowAllOption && (
            <button
              type="button"
              className="text-xs text-muted-foreground underline-offset-2 hover:underline disabled:opacity-50"
              onClick={() => onSelectedValueChange("")}
              disabled={disabled || selectedValue === ""}
            >
              {allOptionLabel}
            </button>
          )}
          <PagedCombobox
            title={cropTypeLabel}
            value={selectedItem}
            onChange={(item) => onSelectedValueChange(item?.id ?? "")}
            fetchPage={fetchPage}
            disabled={disabled}
            pageSize={pageSize}
          />
        </div>
      </Field>
    </div>
  );
};
